/**
 * El recargo por tardanza que se va a cobrar, preguntado al servidor.
 *
 * POR QUÉ NO SE CALCULA ACÁ. La regla ya vive en dos lugares
 * (`compute_rental_late_fee` en SQL, `calculateLateFeeCore` en TypeScript) y el
 * que COBRA es el de SQL, disparado por `trg_apply_late_fee_on_completion`.
 * Calcularlo una tercera vez para mostrarlo es la forma exacta de terminar
 * enseñando un número distinto del que se cobra — y en la pantalla de
 * devolución, eso es una promesa rota sobre dinero.
 *
 * Se refresca solo: la mora depende del reloj, así que un número congelado en
 * pantalla mientras alguien llena la devolución también miente.
 */
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

export interface LateFeePreview {
  /** Cuándo vencía el viaje. */
  endAt: string
  /** Minutos de retraso a este momento. 0 si todavía no vence. */
  minutesLate: number
  /** Lo que se cobraría si el viaje se cerrara ahora. */
  feeCents: number
  /** Hasta cuándo no hay recargo. */
  graceEndsAt: string | null
  /** Lo que se cobra apenas se pasa la cortesía. */
  feeAfterGraceCents: number
}

export const lateFeeKeys = {
  preview: (rentalId: string) => ['late-fee', 'preview', rentalId] as const,
}

export function useLateFeePreview(rentalId: string | undefined) {
  return useQuery<LateFeePreview | null>({
    queryKey: lateFeeKeys.preview(rentalId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('late_fee_preview', { p_rental_id: rentalId! })
      if (error) throw ServiceError.fromSupabaseError(error)

      const row = (data ?? [])[0]
      if (!row) return null

      return {
        endAt: row.end_at,
        minutesLate: row.minutes_late,
        feeCents: row.fee_cents,
        graceEndsAt: row.grace_ends_at,
        feeAfterGraceCents: row.fee_after_grace_cents,
      }
    },
    enabled: !!rentalId,
    // Un minuto: suficiente para que el número siga siendo cierto mientras
    // alguien llena el formulario, sin castigar la red por estar ahí parado.
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    // Nunca bloquea nada: si no se puede saber, la devolución sigue igual.
    retry: 1,
  })
}
